import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Bath, Bed, Ruler, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { fmtMoney, parseMoney } from "@/lib/calculators";
import {
  LISTING_STATUS_LABELS,
  LISTING_STATUS_PILL,
  blocksAcquisition,
  type ListingStatus,
} from "@/lib/listing-status";
import type { Strategy } from "@/types/pipeline";
import { StrategyBadge } from "./StrategyBadge";

interface KanbanCardData {
  id: string;
  strategy: Strategy | null;
  /**
   * Pre-acquisition market status from the feed. Resolved by the board from
   * `pipeline_properties.listing_status`, so it's always a known value.
   */
  listingStatus: ListingStatus;
  addr1?: string | null;
  addr2?: string | null;
  listedPrice?: string | number | null;
  purchasePrice?: number | null;
  bd?: number | null;
  ba?: number | null;
  sqft?: string | number | null;
  imgUrl?: string | null;
}

interface KanbanCardProps {
  card: KanbanCardData;
  /**
   * The card sits in the Closed-Won column. It renders greyed-out to mark the
   * deal as closed, but stays draggable so a mis-drop can be corrected.
   */
  isClosedWon?: boolean;
  /**
   * A Closed-Won drop is waiting on the live market-status check. The card is
   * dimmed and inert so it's obvious which deal is being confirmed and that a
   * second drop won't do anything yet.
   */
  isCheckingStatus?: boolean;
  onClick?: (id: string) => void;
  /**
   * Remove the deal from the pipeline. When provided, a remove button appears on
   * cards whose listing sold to another buyer — the deal is dead, so the card is
   * just clutter. The handler is expected to confirm before deleting.
   */
  onRemove?: (id: string) => void;
}

function titleCase(s: string | null | undefined): string {
  if (!s) return "";
  return String(s)
    .split(" ")
    .map((word) => {
      if (word.length <= 2 && /^[A-Z]+$/.test(word.replace(/[,.]/g, "")))
        return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function KanbanCard({
  card,
  isClosedWon,
  isCheckingStatus,
  onClick,
  onRemove,
}: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `card:${card.id}`,
      data: { dealId: card.id, strategy: card.strategy },
    });

  const style = { transform: CSS.Translate.toString(transform) };

  // "Sold" means sold to someone else — the deal is dead, so the photo greys
  // out. pending/contingent stay full-colour: they're a warning, not an ending.
  const isSold = blocksAcquisition(card.listingStatus);
  const statusLabel = LISTING_STATUS_LABELS[card.listingStatus];
  const statusPill = LISTING_STATUS_PILL[card.listingStatus];

  // sqft may arrive as a number or a string ("0", "1,727"); format numerically
  // when possible, otherwise fall back to the raw value.
  const sqftNum = typeof card.sqft === "number" ? card.sqft : Number(card.sqft);
  const sqftLabel = Number.isFinite(sqftNum) ? sqftNum.toLocaleString() : String(card.sqft ?? 0);

  const listed = parseMoney(card.listedPrice ?? 0);
  const purchase = card.purchasePrice ?? null;
  const showOffer = purchase !== null && listed > 0 && purchase !== listed;
  const deltaPct = showOffer ? ((purchase - listed) / listed) * 100 : 0;
  const deltaSign = deltaPct > 0 ? "+" : "";
  const deltaCls =
    deltaPct < 0
      ? "text-success bg-success/15"
      : "text-destructive bg-destructive/15";

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onClick?.(card.id)}
      role="button"
      aria-label={`Deal: ${card.addr1 ?? "unnamed"}`}
      aria-busy={isCheckingStatus || undefined}
      className={cn(
        "relative flex-shrink-0 cursor-grab overflow-hidden rounded-lg border border-border bg-card transition-all select-none",
        "hover:border-border/60 hover:shadow-md",
        isDragging && "rotate-1 scale-[0.97] opacity-40 cursor-grabbing",
        // Closed-Won: dimmed to mark the deal as closed, but still draggable.
        isClosedWon && "opacity-60",
        // Confirming the listing against the feed — inert until it resolves.
        isCheckingStatus && "pointer-events-none animate-pulse opacity-50",
      )}
    >
      {/* Photo hero */}
      {card.imgUrl ? (
        <div
          className={cn(
            "relative h-[140px] bg-cover bg-center",
            isSold && "grayscale",
          )}
          style={{ backgroundImage: `url('${card.imgUrl}')` }}
          aria-hidden
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />

          {/* Market status badge — top left */}
          <span
            className={cn(
              "absolute left-2 top-2 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
              statusPill,
            )}
          >
            {statusLabel}
          </span>

          {/* Strategy badge — bottom right */}
          {card.strategy && (
            <div className="absolute bottom-2 right-2">
              <StrategyBadge strategy={card.strategy} />
            </div>
          )}
        </div>
      ) : (
        /* No photo placeholder */
        <div className="relative h-16 bg-muted flex items-center justify-center">
          <span
            className={cn(
              "absolute left-2 top-2 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
              statusPill,
            )}
          >
            {statusLabel}
          </span>
          {card.strategy && (
            <div className="absolute bottom-2 right-2">
              <StrategyBadge strategy={card.strategy} />
            </div>
          )}
        </div>
      )}

      {/*
        Remove — only for listings that sold to another buyer. The deal can never
        be closed, so the card is dead weight on the board.

        `stopPropagation` on pointerDown is what keeps this clickable: dnd-kit's
        PointerSensor activates the drag on pointerdown, so without it the button
        would only ever start a drag. The click handler stops propagation too, or
        the card's own onClick would also open the detail view.
      */}
      {isSold && onRemove && (
        <Button
          variant="destructive"
          size="icon"
          aria-label={`Remove ${card.addr1 ?? "this deal"} from pipeline`}
          className="absolute right-2 top-2 z-10 h-7 w-7"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(card.id);
          }}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </Button>
      )}

      {/* Card body */}
      <div className="p-3 space-y-1.5">
        {/* Address */}
        <div>
          <div className="truncate text-[13px] font-bold leading-tight text-foreground">
            {titleCase(card.addr1) || "Unknown Address"}
          </div>
          {card.addr2 && (
            <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
              {titleCase(card.addr2)}
            </div>
          )}
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2">
          {showOffer && purchase !== null ? (
            <>
              <span
                className={cn(
                  "text-[15px] font-extrabold",
                  deltaPct < 0 ? "text-success" : "text-destructive",
                )}
              >
                {fmtMoney(purchase)}
              </span>
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                  deltaCls,
                )}
              >
                {deltaSign}
                {deltaPct.toFixed(1)}%
              </span>
            </>
          ) : (
            <span className="text-[15px] font-extrabold text-foreground">
              {card.listedPrice ? fmtMoney(parseMoney(card.listedPrice)) : ""}
            </span>
          )}
        </div>
        {showOffer && card.listedPrice && (
          <div className="text-[11px] text-muted-foreground -mt-1">
            Listed{" "}
            <span className="line-through">
              {fmtMoney(parseMoney(card.listedPrice))}
            </span>
          </div>
        )}

        {/* Specs — always show beds/baths/sqft together (incl. zeros), like the
            Find PropertyCard. Hidden only when the card carries no spec data. */}
        {(card.bd != null || card.ba != null || card.sqft != null) && (
          <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Bed className="h-3 w-3" /> {card.bd ?? 0} beds
            </span>
            <span className="inline-flex items-center gap-1">
              <Bath className="h-3 w-3" /> {card.ba ?? 0} baths
            </span>
            <span className="inline-flex items-center gap-1">
              <Ruler className="h-3 w-3" /> {sqftLabel} sqft
            </span>
          </div>
        )}

      </div>
    </div>
  );
}
